/**
 * TabBar — Photoshop-style document tabs.
 * Sits between TopActionBar and the canvas.
 */
import React, { useState, useRef } from 'react';
import { useTabsStore } from '../store/useTabsStore';

export default function TabBar() {
  const tabs = useTabsStore(s => s.tabs);
  const activeTabId = useTabsStore(s => s.activeTabId);
  const switchTab = useTabsStore(s => s.switchTab);
  const closeTab = useTabsStore(s => s.closeTab);
  const addTab = useTabsStore(s => s.addTab);
  const renameTab = useTabsStore(s => s.renameTab);

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [dragId, setDragId] = useState(null);
  const inputRef = useRef(null);

  const handleDoubleClick = (tab) => {
    setEditingId(tab.id);
    setEditValue(tab.name);
    setTimeout(() => inputRef.current?.select(), 50);
  };

  const handleRenameSubmit = () => {
    if (editingId && editValue.trim()) {
      renameTab(editingId, editValue.trim());
    }
    setEditingId(null);
  };

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    if (dragId && dragId !== targetId) {
      const { tabs, reorderTabs } = useTabsStore.getState();
      const from = tabs.findIndex(t => t.id === dragId);
      const to = tabs.findIndex(t => t.id === targetId);
      if (from !== -1 && to !== -1) reorderTabs(from, to);
    }
  };

  const handleDragEnd = () => setDragId(null);

  const handleMiddleClick = (e, id) => {
    if (e.button === 1) { // Middle click
      e.preventDefault();
      closeTab(id);
    }
  };

  return (
    // `scrollbar-none` était une classe MORTE : aucun plugin scrollbar n'est
    // déclaré dans tailwind.config.js, elle ne produisait aucune règle. Elle
    // laissait croire que la barre était masquée volontairement, alors que la
    // rangée hérite simplement de la barre globale de 4 px (globals.css) — d'où
    // l'impression qu'elle défile sans rien montrer. Classe retirée pour que le
    // comportement réel soit celui qu'on lit.
    <div className="flex items-center bg-black/20 border-b border-forge-border/15 h-[30px] shrink-0 select-none overflow-x-auto">
      {tabs.map(tab => {
        const isActive = tab.id === activeTabId;
        const isDragging = tab.id === dragId;

        return (
          <div
            key={tab.id}
            draggable={editingId !== tab.id}
            onDragStart={(e) => handleDragStart(e, tab.id)}
            onDragOver={(e) => handleDragOver(e, tab.id)}
            onDragEnd={handleDragEnd}
            onMouseDown={(e) => handleMiddleClick(e, tab.id)}
            onClick={() => switchTab(tab.id)}
            onDoubleClick={() => handleDoubleClick(tab)}
            className={`
              relative flex items-center gap-1.5 px-3 h-full text-[10px] font-mono cursor-pointer
              transition-all duration-150 shrink-0 max-w-[180px] min-w-[100px] group
              border-r border-forge-border/10
              ${isActive
                ? tab.isDirty
                  ? 'bg-red-900/30 text-forge-text border-t-2 border-t-red-500'
                  : 'bg-forge-bg text-forge-text border-t-2 border-t-forge-accent'
                : tab.isDirty
                  ? 'bg-red-900/15 text-red-300/70 hover:bg-red-900/25 hover:text-red-200'
                  : 'bg-transparent text-forge-muted/60 hover:text-forge-text/80 hover:bg-white/5'
              }
              ${isDragging ? 'opacity-50' : ''}
            `}
          >
            {/* Tab name or rename input */}
            {editingId === tab.id ? (
              <input
                ref={inputRef}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleRenameSubmit}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleRenameSubmit();
                  if (e.key === 'Escape') setEditingId(null);
                }}
                className="bg-black/30 border border-forge-accent/40 rounded px-1 py-0.5 text-[10px] text-forge-text outline-none w-full font-mono"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              // L'onglet est plafonné à 180 px et le nom coupé par `truncate`, sans
              // qu'aucun onglet ne porte de `title` : la fin d'un nom long
              // (« Fraise-Kiwi 10 ml nicoti... ») n'était lisible NULLE PART, il
              // fallait double-cliquer pour passer en renommage juste pour la voir.
              // L'infobulle donne le nom complet, et dit l'état modifié en mots
              // plutôt que par l'astérisque, muette pour qui ne connaît pas le code.
              <span
                className="truncate flex-1 tracking-wider uppercase font-bold text-[9px]"
                title={tab.isDirty ? `${tab.name} (modifications non enregistrées)` : tab.name}
              >
                {tab.name}
                {tab.isDirty && <span className="text-red-400 ml-1">*</span>}
              </span>
            )}

            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
              className={`w-4 h-4 rounded flex items-center justify-center shrink-0 transition-all
                ${isActive
                  ? 'text-forge-muted/40 hover:text-forge-rose hover:bg-forge-rose/15'
                  : 'text-transparent group-hover:text-forge-muted/30 hover:!text-forge-rose hover:!bg-forge-rose/15'
                }
              `}
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        );
      })}

      {/* Add new tab button */}
      <button
        onClick={() => addTab()}
        className="w-[28px] h-full flex items-center justify-center text-forge-muted/30 hover:text-forge-mint hover:bg-forge-mint/8 transition-all shrink-0"
        title="Nouveau document" aria-label="Nouveau document"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      </button>
    </div>
  );
}
